import React, { FC, useState } from 'react'

import Modal from './Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { fetchContract, updateContract } from '../../../states/containers/setting/companySlice'
import { uniqueId } from 'lodash'
import { jsPDF } from "jspdf"
import { domToCanvas } from 'modern-screenshot'
import { endOfMonth, format, nowDate } from '../../../utils/date'
import LoadingSquare from './LoadingSquare'
import { getContractResponse } from '../../../states/types/setting'

interface Props {
  display: boolean,
  closeCallback: () => void
}

const OPTION = [
  {
    label: "ai_work_assessment",
    name: "AI業務分析",
  },
  {
    label: "organizational_analysis",
    name: "組織分析",
  },
  {
    label: "risk_assessment",
    name: "リスクアセスメント",
  },
  {
    label: "self_management",
    name: "セルフマネジメント",
  },
]

const ExtraOrderModal: FC<Props> = (props) => {
  const {
    display,
    closeCallback,
  } = props

  const dispatch = useDispatch()

  const {
    login,
    commonPart,
  } = useSelector((state: RootState) => (state))

  const [step, setStep] = useState<string>("select")

  const [checkedOption, setCheckedOption] = useState<string[]>([])
  const [addMemberNum, setAddMemberNum] = useState<number>(0)
  const [accept, setAccept] = useState<boolean>(false)

  const [pdfData, setPdfData] = useState<getContractResponse>()

  const [isPdfLoading, setIsPdfLoading] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const closeModal = () => {
    if (["load_pdf", "load_submit"].includes(step) && isLoading) return
    closeCallback()
    setPdfData(undefined)
    if (step === "complete") {
      location.reload()
    } else {
      setStep("select")
      setCheckedOption([])
      setAddMemberNum(0)
      setAccept(false)
    }
  }

  const changeCheckedOption = (targetLabel: string) => {
    if (checkedOption.includes(targetLabel)) {
      setCheckedOption(checkedOption.filter((v) => v !== targetLabel))
    } else {
      setCheckedOption([...checkedOption, targetLabel])
    }
  }

  const buildOption = (targetLabel: string, contract: boolean) => {
    const target = OPTION.find((v) => v.label === targetLabel)
    if (!target) return (<></>)
    if (contract) {
      return (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "14px",
              color: "#999999",
            }}
          >
            <div>
              <FontAwesomeIcon icon={['far', 'check-square']} />
            </div>
            <div>
              {target.name}
            </div>
            <div
              style={{
                borderRadius: "4px",
                backgroundColor: "#008001",
                fontSize: "10px",
                padding: "2px 4px",
                color: "#FFFFFF",
              }}
            >
              契約済み
            </div>
          </div>
        </div>
      )
    }
    if (targetLabel === "ai_work_assessment") return (<></>)
    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "14px",
            cursor: "pointer",
            color: checkedOption.includes(targetLabel) ? "#008001" : "#333333",
          }}
          onClick={() => changeCheckedOption(targetLabel)}
          role="none"
        >
          <div>
            <FontAwesomeIcon icon={['far', checkedOption.includes(targetLabel) ? 'check-square' : 'square']} />
          </div>
          <div>
            {target.name}
          </div>
        </div>
      </div>
    )
  }

  const calcAmount = () => {
    setIsLoading(true)
    setStep("load_pdf")
    dispatch(
      fetchContract(
        {
          added_member_count: addMemberNum,
          added_options: checkedOption,
        },
        (result: getContractResponse) => {
          setPdfData(result)
          setStep("preview")
          setIsLoading(false)
        },
        () => {
          setIsLoading(false)
        },
      ),
    )
  }

  const generatePdf = async (element: HTMLElement) => {
    const main = document.querySelector(".main") as HTMLElement
    if (main) main.style.zoom = "100%"
    // eslint-disable-next-line new-cap
    const doc = new jsPDF('p', 'mm', 'a4', true)

    // A4サイズのPDFの横幅
    const pdfWidth = doc.internal.pageSize.getWidth()

    // ElementをCanvas化
    const canvas = await domToCanvas(element)
    doc.addImage(canvas, 'PNG', 2, 10, pdfWidth - 10, 0)

    doc.save(`【Qasee】御見積書_${nowDate("YYYY年M月D日")}_${nowDate("X")}.pdf`)
    setIsPdfLoading(false)
    if (main) main.style.zoom = "97%"
  }

  const downloadPdf = () => {
    setIsPdfLoading(true)
    setTimeout(() => {
      const input = document.getElementById("extra_order_table")
      if (!input) return
      generatePdf(input)
    }, 1000)
  }

  const submit = () => {
    setIsLoading(true)
    setStep("load_submit")
    dispatch(
      updateContract(
        {
          added_member_count: addMemberNum,
          added_options: checkedOption,
          accept: accept,
        },
        () => {
          setTimeout(() => {
            setStep("complete")
            setIsLoading(false)
          }, 3000)
        },
        () => {
          setIsLoading(false)
        },
      ),
    )
  }

  return (
    <>
      <Modal
        show={display}
        wrapperClass="extra_order"
        closeCallback={closeModal}
        closeable
      >
        <>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              marginBottom: "24px",
            }}
          >
            契約の変更
          </div>
          {
            step === "select" && (
              <>
                <div
                  style={{
                    marginBottom: "18px",
                  }}
                >
                  ユーザー上限の追加やオプションの追加ができます。
                </div>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                    border: "solid 1px #CCCCCC",
                    borderRadius: "4px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "8px",
                      borderBottom: "solid 1px #CCCCCC",
                    }}
                  >
                    <div
                      style={{
                        width: "140px",
                      }}
                    >
                      ユーザーの追加
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        fontSize: "14px",
                        width: "calc(100% - 140px)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "2px",
                        }}
                      >
                        <div>
                          現在：
                        </div>
                        <div
                          style={{
                            fontSize: "22px",
                            color: "#666666",
                          }}
                        >
                          {login.limit_member_number?.toLocaleString()}
                        </div>
                        <div>
                          名
                        </div>
                      </div>
                      <FontAwesomeIcon icon={['fas', 'plus']} />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          gap: "2px",
                        }}
                      >
                        <input
                          type="number"
                          min={0}
                          step={1}
                          style={{ width: "60px", padding: "4px 8px" }}
                          value={addMemberNum}
                          onChange={(e) => setAddMemberNum(Number(e.currentTarget.value))}
                        />
                        <div>
                          名追加
                        </div>
                      </div>
                      <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "22px",
                            color: "#008001",
                          }}
                        >
                          {((login.limit_member_number || 0) + addMemberNum).toLocaleString()}
                        </div>
                        <div>
                          名
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      padding: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "140px",
                      }}
                    >
                      オプションの追加
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                        width: "calc(100% - 140px)",
                      }}
                    >
                      {buildOption("organizational_analysis", login.function_enabled_state?.organizational_analysis || false)}
                      {buildOption("ai_work_assessment", login.function_enabled_state?.ai_work_assessment || false)}
                      {buildOption("risk_assessment", login.function_enabled_state?.risk_assessment || false)}
                      {buildOption("self_management", login.function_enabled_state?.self_management || false)}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={calcAmount}
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      backgroundColor: "#EEEEEE",
                    }}
                    disabled={!(addMemberNum || checkedOption.length)}
                  >
                    次へ
                  </button>
                </div>
              </>
            )
          }
          {
            step === "preview" && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                        marginBottom: "12px",
                      }}
                    >
                      追加内容の確認
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        gap: "4px",
                      }}
                    >
                      {
                        !!addMemberNum && (
                          <div
                            style={{
                              display: "flex",
                              borderBottom: "solid 1px #CCCCCC",
                              padding: "4px",
                            }}
                          >
                            <div
                              style={{
                                width: "200px",
                              }}
                            >
                              ユーザーの追加
                            </div>
                            <div>
                              {addMemberNum}
                              名
                            </div>
                          </div>
                        )
                      }
                      {
                        !!checkedOption.length && (
                          <div
                            style={{
                              display: "flex",
                              borderBottom: "solid 1px #CCCCCC",
                              padding: "4px",
                            }}
                          >
                            <div
                              style={{
                                width: "200px",
                              }}
                            >
                              オプションの追加
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "4px",
                              }}
                            >
                              {
                                checkedOption.map((v) => (
                                  <React.Fragment key={v}>
                                    <div>
                                      {`${OPTION.find((w) => w.label === v)?.name}機能`}
                                    </div>
                                  </React.Fragment>
                                ))
                              }
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "baseline",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                        }}
                      >
                        契約変更による追加費用(一ヶ月あたり)
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "bold",
                            fontSize: "16px",
                          }}
                        >
                          {`¥${pdfData?.summary.total.toLocaleString()}`}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="quotation"
                            onClick={downloadPdf}
                          >
                            御見積書のPDFをダウンロード
                          </button>
                        </div>
                      </div>
                    </div>
                    <div id="extra_order_table" style={{ padding: "24px" }}>
                      <div
                        style={{
                          display: isPdfLoading ? "flex" : "none",
                          flexFlow: "column",
                          gap: "24px",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            fontSize: "20px",
                          }}
                        >
                          御見積書
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "14px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                textDecoration: "underline",
                                fontWeight: "bold",
                              }}
                            >
                              {`${commonPart.company?.name} 御中`}
                            </div>
                          </div>
                          <div
                            style={{
                              textAlign: "right",
                            }}
                          >
                            <div>
                              {`見積日：${format(pdfData?.contract_date, "YYYY年M月D日")}`}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                          }}
                        >
                          下記の通り御見積もり申し上げます
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "360px",
                            }}
                          >
                            <table className="extra_order_total_table">
                              <thead>
                                <tr>
                                  <th>小計</th>
                                  <th>消費税</th>
                                  <th>合計金額</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    {`¥${pdfData?.summary.subtotal.toLocaleString()}`}
                                  </td>
                                  <td>
                                    {`¥${pdfData?.summary.tax_amount.toLocaleString()}`}
                                  </td>
                                  <td
                                    style={{
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {`¥${pdfData?.summary.total.toLocaleString()}`}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "left",
                                  display: "flex",
                                  flexFlow: "column",
                                  gap: "4px",
                                  zIndex: 1,
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "14px",
                                  }}
                                >
                                  Qasee株式会社
                                </div>
                                {
                                  pdfData?.qasee_address.map((v) => (
                                    <div
                                      key={`address_${v}`}
                                      style={{
                                        fontSize: "11px",
                                      }}
                                    >
                                      {v}
                                    </div>
                                  ))
                                }
                              </div>
                              <div
                                style={{
                                  marginLeft: "-20px",
                                  zIndex: 0,
                                }}
                              >
                                <img
                                  alt=""
                                  src="/static/images/qasee_stamp.png"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <table className="extra_order_table">
                        <thead>
                          <tr>
                            <th>項目</th>
                            <th>単価</th>
                            <th>数量</th>
                            <th>期間</th>
                            <th>計</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            pdfData?.contract_history_details.map((v) => (
                              <tr
                                key={uniqueId()}
                                style={{
                                  color: v.total < 0 ? "#FF0000" : "#333333",
                                }}
                              >
                                <td>{v.title}</td>
                                <td>
                                  {v.unit < 0 && (<>▲</>)}
                                  {`${Math.abs(v.unit).toLocaleString()}`}
                                </td>
                                <td>{v.count.toLocaleString()}</td>
                                <td>1ヶ月</td>
                                <td>
                                  {v.total < 0 && (<>▲</>)}
                                  {`${Math.abs(v.total).toLocaleString()}`}
                                </td>
                              </tr>
                            ))
                          }
                          <tr>
                            <td />
                            <td />
                            <td />
                            <td />
                            <td />
                          </tr>
                          <tr>
                            <td colSpan={4}>小計…①</td>
                            <td>{`${pdfData?.summary.subtotal.toLocaleString()}`}</td>
                          </tr>
                          <tr>
                            <td colSpan={4}>消費税…②</td>
                            <td>{`${pdfData?.summary.tax_amount.toLocaleString()}`}</td>
                          </tr>
                          <tr
                            style={{
                              borderTop: "solid 2px #333333",
                              fontWeight: "bold",
                            }}
                          >
                            <td colSpan={4}>合計金額 (① + ②)</td>
                            <td>{`${pdfData?.summary.total.toLocaleString()}`}</td>
                          </tr>
                        </tbody>
                      </table>
                      <div
                        style={{
                          display: isPdfLoading ? "flex" : "none",
                          flexFlow: "column",
                          gap: "16px",
                          border: "solid 1px #CCCCCC",
                          padding: "8px",
                          margin: "16px 0",
                        }}
                      >
                        <div
                          style={{
                            textDecoration: "underline",
                          }}
                        >
                          備考欄
                        </div>
                        <div>
                          表示されている金額は一ヶ月あたりの追加金額になります。
                        </div>
                        <div>
                          変更分のご請求書については、翌月初に送付いたします。
                        </div>
                        <div>
                          {`お見積りの有効期限：${endOfMonth(nowDate(), "YYYY年M月D日")}`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      padding: "8px",
                      display: "flex",
                      flexFlow: "column",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "18px",
                        fontWeight: "bold",
                      }}
                    >
                      契約変更の注意事項
                    </div>
                    <div
                      style={{
                        border: "solid 1px #999999",
                        backgroundColor: "#fcfcfc",
                        padding: "8px",
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                      }}
                    >
                      <div>
                        表示されている金額は一ヶ月あたりの追加金額になります。
                      </div>
                      <div>
                        変更分のご請求書については、翌月初に送付いたします。
                      </div>
                      <div>
                        変更にあたり、必ず事前に
                        <a href="https://view.qasee.jp/tos_privacy/tos/" target="_blank" rel="noreferrer">利用規約</a>
                        をご確認ください。
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        border: "solid 1px #008800",
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "#e7fce7",
                        cursor: "pointer",
                      }}
                      onClick={() => setAccept(!accept)}
                      role="none"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          cursor: "pointer",
                          color: accept ? "#008001" : "#333333",
                          fontSize: "14px",
                        }}
                      >
                        <div>
                          <FontAwesomeIcon icon={['far', accept ? 'check-square' : 'square']} />
                        </div>
                        <div>
                          利用規約を確認し、同意します
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setStep("select")}
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      backgroundColor: "#EEEEEE",
                    }}
                  >
                    ひとつ戻る
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      backgroundColor: "#EEEEEE",
                    }}
                    disabled={!accept}
                  >
                    変更する
                  </button>
                </div>
              </>
            )
          }
          {
            step === "complete" && (
              <>
                <div
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      padding: "8px",
                      fontSize: "18px",
                      fontWeight: "bold",
                      border: "solid 1px #008800",
                      backgroundColor: "#e4ffe4",
                      textAlign: "center",
                      color: "#008800",
                      marginBottom: "8px",
                    }}
                  >
                    契約内容の変更が完了しました
                  </div>
                  <div>
                    契約変更内容と契約変更に伴う追加費用が記載されたメールを送信いたしました。
                  </div>
                  <div>
                    引き続きお客様にご満足いただける製品とサービスを提供できるよう、全力で努めてまいります。今後とも変わらぬご愛顧賜りますよう、どうぞよろしくお願い申し上げます。
                  </div>
                </div>
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeModal}
                    style={{
                      border: "solid 1px #CCCCCC",
                      borderRadius: "4px",
                      backgroundColor: "#EEEEEE",
                    }}
                  >
                    閉じる
                  </button>
                </div>
              </>
            )
          }
          {
            ["load_pdf", "load_submit"].includes(step) && (
              <>
                {
                  isLoading ? (
                    <LoadingSquare>
                      <>
                        {
                          step === "load_pdf" ? "見積データを生成しています…" : "変更内容を送信しています…"
                        }
                      </>
                    </LoadingSquare>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexFlow: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                      }}
                    >
                      <div
                        style={{
                          color: "#FF0000",
                          fontSize: "18px",
                        }}
                      >
                        通信エラーが発生しました
                      </div>
                      <div
                        style={{
                          color: "#666666",
                          fontSize: "14px",
                        }}
                      >
                        もう一度はじめからやり直してください
                      </div>
                    </div>
                  )
                }
              </>
            )
          }
        </>
      </Modal>
      <div
        className={`close_screen in_message ${isPdfLoading || 'none'}`}
        role="presentation"
        style={{
          zIndex: 2050,
        }}
      >
        <LoadingSquare color="#FFFFFF">
          <>
            御見積書PDFを生成しています…
          </>
        </LoadingSquare>
      </div>
    </>
  )
}

export default ExtraOrderModal
